import { mainApi, accountingApi } from '../../../../config/apiClient';
import type { InventoryItem } from '../types/inventory.types';

export interface BulkSendProgress {
  currentItem: string;
  progress: number;
  success: number;
  failed: number;
  total: number;
}

export interface BulkSendResult {
  success: number;
  failed: number;
  total: number;
}

/** Company info for QR Menu / integration payloads */
export interface CompanyInfo {
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  taxNumber?: string;
  createdAt?: string;
}

/**
 * QR Menu, Kiosk ve diğer entegrasyonlar için service
 * ✅ Firebase bağımlılığı kaldırıldı - Tüm işlemler SQL API üzerinden yapılır
 */
export class IntegrationService {
  /**
   * Ürünleri QR Menu'ye gönder (SQL API)
   */
  static async sendToQRMenu(
    companyId: string,
    items: InventoryItem[],
    onProgress?: (sent: number, total: number) => void
  ): Promise<void> {
    console.log(`🍽️ [QR MENU] ${items.length} ürün QR Menu'ye gönderiliyor (SQL API)`);
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      try {
        // SQL API'ye gönder
        const response = await mainApi.post<{ success: boolean; message?: string }>('/api/QRMenu/send-stock', {
          companyInfo: {
            firebaseId: companyId,
            name: 'Company'
          },
          stockInfo: {
            firebaseId: item.id,
            name: item.name,
            stockCode: item.stockCode,
            salePrice: item.salePrice,
            purchasePrice: item.purchasePrice || 0,
            // ✅ YENİ SİSTEM: WarehouseStocks'tan toplam hesapla
            quantity: item.warehouseStocks?.reduce((sum, ws) => sum + ws.quantity, 0) || 0,
            unit: item.unit || 'Adet',
            categoryId: item.categoryId || null,
            imageUrl: item.imageUrl || null,
            description: item.description || ''
          }
        });

        const result = response.data;
        
        if (!result.success) {
          console.error(`❌ [QR MENU] ${item.name} gönderilemedi:`, result.message);
        } else {
          console.log(`✅ [QR MENU] ${item.name} başarıyla gönderildi`);
        }
        
        // ✅ SQL API ile QR Menü flag'ini güncelle (Firebase yerine)
        try {
          await accountingApi.post(
            `/api/Inventory/${item.id}/toggle-qrmenu`,
            { enable: true }
          );
        } catch (toggleError) {
          console.error(`❌ [QR MENU] ${item.name} SQL toggle hatası:`, toggleError);
        }
        
      } catch (error) {
        console.error(`❌ [QR MENU] ${item.name} için hata:`, error);
      }
      
      if (onProgress) {
        onProgress(i + 1, items.length);
      }
    }
    
    console.log(`✅ [QR MENU] Tüm ürünler işlendi`);
  }

  /**
   * Ürünleri QR Menu'den kaldır
   */
  static async removeFromQRMenu(
    companyId: string,
    itemIds: string[]
  ): Promise<void> {
    for (const itemId of itemIds) {
      try {
        // ✅ SQL API ile QR Menü flag'ini kapat (Firebase yerine)
        await accountingApi.post(
          `/api/Inventory/${itemId}/toggle-qrmenu`,
          { enable: false }
        );
      } catch (error) {
        console.error(`❌ [QR MENU] ${itemId} kaldırılırken hata:`, error);
      }
    }
  }

  /**
   * Ürünleri Kiosk'a gönder (SQL API)
   */
  static async sendToKiosk(
    companyId: string,
    items: InventoryItem[],
    onProgress?: (sent: number, total: number) => void
  ): Promise<void> {
    console.log(`🖥️ [KIOSK] ${items.length} ürün Kiosk'a gönderiliyor`);
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      try {
        // ✅ SQL API ile Kiosk flag'ini aç (Firebase yerine)
        await accountingApi.post(
          `/api/Inventory/${item.id}/toggle-kiosk`,
          { enable: true }
        );
        console.log(`✅ [KIOSK] ${item.name} başarıyla gönderildi`);
      } catch (error) {
        console.error(`❌ [KIOSK] ${item.name} gönderilirken hata:`, error);
      }
      
      if (onProgress) {
        onProgress(i + 1, items.length);
      }
    }
    
    console.log(`✅ [KIOSK] ${items.length} ürün başarıyla gönderildi`);
  }

  /**
   * Ürünleri Kiosk'tan kaldır
   */
  static async removeFromKiosk(
    companyId: string,
    itemIds: string[]
  ): Promise<void> {
    for (const itemId of itemIds) {
      try {
        // ✅ SQL API ile Kiosk flag'ini kapat (Firebase yerine)
        await accountingApi.post(
          `/api/Inventory/${itemId}/toggle-kiosk`,
          { enable: false }
        );
      } catch (error) {
        console.error(`❌ [KIOSK] ${itemId} kaldırılırken hata:`, error);
      }
    }
  }

  /**
   * QR Menu ürün durumunu kontrol et (SQL API)
   */
  static async checkQRMenuStatus(
    companyId: string,
    itemId: string
  ): Promise<boolean> {
    try {
      // ✅ SQL API'den ürünü çek ve isQRMenu flag'ini kontrol et (Firebase yerine)
      const response = await accountingApi.get(
        `/api/Inventory/items/${companyId}/${itemId}`
      );
      return response.data?.isQRMenu === true;
    } catch (error) {
      console.error(`❌ [QR MENU] ${itemId} status kontrol hatası:`, error);
      return false;
    }
  }

  /**
   * Kiosk ürün durumunu kontrol et (SQL API)
   */
  static async checkKioskStatus(
    companyId: string,
    itemId: string
  ): Promise<boolean> {
    try {
      // ✅ SQL API'den ürünü çek ve isKiosk flag'ini kontrol et (Firebase yerine)
      const response = await accountingApi.get(
        `/api/Inventory/items/${companyId}/${itemId}`
      );
      return response.data?.isKiosk === true;
    } catch (error) {
      console.error(`❌ [KIOSK] ${itemId} status kontrol hatası:`, error);
      return false;
    }
  }

  /**
   * QR Menu'deki tüm ürünleri al (SQL API)
   */
  static async getQRMenuProducts(companyId: string): Promise<any[]> {
    try {
      // ✅ SQL API'den tüm ürünleri çek ve isQRMenu olanları filtrele (Firebase yerine)
      const response = await accountingApi.get(
        `/api/Inventory/items/${companyId}`
      );
      const allItems = response.data || [];
      return allItems.filter((item: any) => item.isQRMenu === true);
    } catch (error) {
      console.error(`❌ [QR MENU] Ürünler alınırken hata:`, error);
      return [];
    }
  }

  /**
   * Kiosk'taki tüm ürünleri al (SQL API)
   */
  static async getKioskProducts(companyId: string): Promise<any[]> {
    try {
      // ✅ SQL API'den tüm ürünleri çek ve isKiosk olanları filtrele (Firebase yerine)
      const response = await accountingApi.get(
        `/api/Inventory/items/${companyId}`
      );
      const allItems = response.data || [];
      return allItems.filter((item: any) => item.isKiosk === true);
    } catch (error) {
      console.error(`❌ [KIOSK] Ürünler alınırken hata:`, error);
      return [];
    }
  }

  /**
   * QR Menu ve Kiosk'u senkronize et (SQL API)
   */
  static async syncIntegrations(
    companyId: string,
    items: InventoryItem[]
  ): Promise<void> {
    for (const item of items) {
      try {
        // ✅ SQL API ile flag'leri doğrula (Firebase yerine)
        // QR Menu'de aktif ise flag'i güncelle
        if (item.isQRMenu) {
          await accountingApi.post(
            `/api/Inventory/${item.id}/toggle-qrmenu`,
            { enable: true }
          );
        }
        
        // Kiosk'ta aktif ise flag'i güncelle
        if (item.isKiosk) {
          await accountingApi.post(
            `/api/Inventory/${item.id}/toggle-kiosk`,
            { enable: true }
          );
        }
      } catch (error) {
        console.error(`❌ [SYNC] ${item.name} senkronizasyon hatası:`, error);
      }
    }
  }

  /**
   * Toplu QR Menu gönderme - .NET API ile
   * @param companyId - Şirket ID
   * @param items - Gönderilecek ürünler
   * @param inventoryItems - Tüm inventory listesi
   * @param getCategoryName - Kategori adı getiren fonksiyon
   * @param getBrandName - Marka adı getiren fonksiyon
   * @param onProgressUpdate - İlerleme callback'i
   * @param companyInfo - Şirket bilgileri (opsiyonel - AuthContext'ten sağlanabilir)
   * @returns BulkSendResult
   */
  static async bulkSendToQRMenu(
    companyId: string,
    items: InventoryItem[],
    inventoryItems: InventoryItem[],
    getCategoryName: (id: string | null) => string,
    getBrandName: (id: string | null) => string,
    onProgressUpdate?: (progress: BulkSendProgress) => void,
    companyInfo?: CompanyInfo
  ): Promise<BulkSendResult> {
    // ✅ companyInfo parametreden alınır (Firebase yerine)
    const info = companyInfo || { name: 'Bilinmeyen Şirket' };
    
    let successCount = 0;
    let failedCount = 0;

    // Batch processing - Rate limiting'i önlemek için gruplar halinde işle
    const batchSize = 10; // Her grupta 10 ürün
    const delayBetweenBatches = 2000; // Gruplar arası 2 saniye bekleme
    const delayBetweenItems = 200; // Ürünler arası 200ms bekleme
    
    for (let batchStart = 0; batchStart < items.length; batchStart += batchSize) {
      const batchEnd = Math.min(batchStart + batchSize, items.length);
      const currentBatch = items.slice(batchStart, batchEnd);
      
      console.log(`🔄 [BULK QR MENU] Grup ${Math.floor(batchStart / batchSize) + 1}/${Math.ceil(items.length / batchSize)} işleniyor (${currentBatch.length} ürün)`);
      
      // Mevcut grubu işle
      for (let i = 0; i < currentBatch.length; i++) {
        const item = currentBatch[i];
        const globalIndex = batchStart + i;
        
        if (onProgressUpdate) {
          onProgressUpdate({
            currentItem: `[${globalIndex + 1}/${items.length}] ${item.name}`,
            progress: Math.round(((globalIndex + 1) / items.length) * 100),
            success: successCount,
            failed: failedCount,
            total: items.length
          });
        }
        
        try {
          // .NET API'ye gönderilecek payload
          const payload = {
            // Şirket bilgileri
            companyInfo: {
              firebaseId: companyId,
              name: info.name || 'Bilinmeyen Şirket',
              email: info.email || '',
              phone: info.phone || '',
              address: info.address || '',
              taxNumber: info.taxNumber || '',
              createdAt: info.createdAt ? new Date(info.createdAt).toISOString() : new Date().toISOString()
            },
            // Stok bilgileri
            stockInfo: {
              firebaseId: item.id,
              name: item.name,
              stockCode: item.stockCode,
              barcode: item.barcode || null,
              categoryName: getCategoryName(item.categoryId || null),
              brandName: getBrandName(item.brandId || null),
              // ✅ YENİ SİSTEM: WarehouseStocks'tan toplam hesapla
              quantity: item.warehouseStocks?.reduce((sum, ws) => sum + ws.quantity, 0) || 0,
              unit: item.unit,
              salePrice: item.salePrice,
              saleCurrency: item.saleCurrency || 'TRY',
              description: item.description || '',
              imageUrl: item.imageUrl || null,
              isActive: item.isActive,
              createdAt: item.createdAt ? new Date(item.createdAt).toISOString() : new Date().toISOString()
            }
          };

          // .NET API'ye istek gönder
          await mainApi.post('/api/QRMenu/send-stock', payload);

          successCount++;
          console.log(`✅ [BULK QR MENU] ${item.name} başarıyla gönderildi`);
          
          // ✅ SQL API ile QR Menü flag'ini güncelle (Firebase yerine)
          try {
            await accountingApi.post(
              `/api/Inventory/${item.id}/toggle-qrmenu`,
              { enable: true }
            );
          } catch (toggleError) {
            console.error(`❌ [BULK QR MENU] ${item.name} SQL toggle hatası:`, toggleError);
            // SQL toggle hatası ana işlemi etkilemesin
          }
        } catch (error: any) {
          failedCount++;
          console.error(`❌ [BULK QR MENU] ${item.name} gönderilirken hata:`, error);
          // 429 hatası alındıysa daha uzun bekle
          if (error.response?.status === 429) {
            console.warn(`⚠️ [BULK QR MENU] Rate limit aşıldı, 3 saniye bekleniyor...`);
            await new Promise(resolve => setTimeout(resolve, 3000));
          }
        }
        
        // Ürünler arası kısa bekleme
        if (i < currentBatch.length - 1) {
          await new Promise(resolve => setTimeout(resolve, delayBetweenItems));
        }
      }
      
      // Gruplar arası uzun bekleme (son grup değilse)
      if (batchEnd < items.length) {
        if (onProgressUpdate) {
          onProgressUpdate({
            currentItem: `Sonraki grup için bekleniyor... (${Math.floor(delayBetweenBatches / 1000)} saniye)`,
            progress: Math.round((batchEnd / items.length) * 100),
            success: successCount,
            failed: failedCount,
            total: items.length
          });
        }
        await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
      }
    }

    // İşlem tamamlandı
    if (onProgressUpdate) {
      onProgressUpdate({
        currentItem: 'İşlem tamamlandı!',
        progress: 100,
        success: successCount,
        failed: failedCount,
        total: items.length
      });
    }

    return { success: successCount, failed: failedCount, total: items.length };
  }

  /**
   * Toplu Kiosk gönderme - .NET API ile
   * @param companyId - Şirket ID
   * @param items - Gönderilecek ürünler
   * @param inventoryItems - Tüm inventory listesi
   * @param getCategoryName - Kategori adı getiren fonksiyon
   * @param getBrandName - Marka adı getiren fonksiyon
   * @param onProgressUpdate - İlerleme callback'i
   * @returns BulkSendResult
   */
  static async bulkSendToKiosk(
    companyId: string,
    items: InventoryItem[],
    inventoryItems: InventoryItem[],
    getCategoryName: (id: string | null) => string,
    getBrandName: (id: string | null) => string,
    onProgressUpdate?: (progress: BulkSendProgress) => void
  ): Promise<BulkSendResult> {
    let successCount = 0;
    let failedCount = 0;

    // Batch processing - Rate limiting'i önlemek için gruplar halinde işle
    const batchSize = 10; // Her grupta 10 ürün
    const delayBetweenBatches = 2000; // Gruplar arası 2 saniye bekleme
    const delayBetweenItems = 200; // Ürünler arası 200ms bekleme
    
    for (let batchStart = 0; batchStart < items.length; batchStart += batchSize) {
      const batchEnd = Math.min(batchStart + batchSize, items.length);
      const currentBatch = items.slice(batchStart, batchEnd);
      
      console.log(`🔄 [BULK KIOSK] Grup ${Math.floor(batchStart / batchSize) + 1}/${Math.ceil(items.length / batchSize)} işleniyor (${currentBatch.length} ürün)`);
      
      // Mevcut grubu işle
      for (let i = 0; i < currentBatch.length; i++) {
        const item = currentBatch[i];
        const globalIndex = batchStart + i;
        
        if (onProgressUpdate) {
          onProgressUpdate({
            currentItem: `[${globalIndex + 1}/${items.length}] ${item.name}`,
            progress: Math.round(((globalIndex + 1) / items.length) * 100),
            success: successCount,
            failed: failedCount,
            total: items.length
          });
        }
        
        try {
          // .NET API'ye gönderilecek payload
          const payload = {
            companyId: companyId,
            firebaseProductId: item.id,
            name: item.name,
            stockCode: item.stockCode,
            barcode: item.barcode || null,
            categoryName: getCategoryName(item.categoryId || null),
            brandName: getBrandName(item.brandId || null),
            quantity: 1, // Toplu işlemde adet = 1
            unit: item.unit,
            salePrice: item.salePrice,
            saleCurrency: item.saleCurrency || 'TRY',
            purchasePrice: item.purchasePrice || null,
            purchaseCurrency: item.purchaseCurrency || 'TRY',
            vatRate: item.vatRate || null,
            vatStatus: item.vatStatus || null,
            departmentNo: (item as any).departmentNo || null, // Yazarkasa departman numarası
            description: item.description || '',
            imageUrl: item.imageUrl || null,
            isActive: item.isActive,
            deductFromStock: false // Stoktan düşme
          };

          // .NET API'ye istek gönder
          await mainApi.post('/api/Kiosk/products/send', payload);

          successCount++;
          console.log(`✅ [BULK KIOSK] ${item.name} başarıyla gönderildi`);
        } catch (error: any) {
          failedCount++;
          console.error(`❌ [BULK KIOSK] ${item.name} gönderilirken hata:`, error);
          // 429 hatası alındıysa daha uzun bekle
          if (error.response?.status === 429) {
            console.warn(`⚠️ [BULK KIOSK] Rate limit aşıldı, 3 saniye bekleniyor...`);
            await new Promise(resolve => setTimeout(resolve, 3000));
          }
        }
        
        // Ürünler arası kısa bekleme
        if (i < currentBatch.length - 1) {
          await new Promise(resolve => setTimeout(resolve, delayBetweenItems));
        }
      }
      
      // Gruplar arası uzun bekleme (son grup değilse)
      if (batchEnd < items.length) {
        if (onProgressUpdate) {
          onProgressUpdate({
            currentItem: `Sonraki grup için bekleniyor... (${Math.floor(delayBetweenBatches / 1000)} saniye)`,
            progress: Math.round((batchEnd / items.length) * 100),
            success: successCount,
            failed: failedCount,
            total: items.length
          });
        }
        await new Promise(resolve => setTimeout(resolve, delayBetweenBatches));
      }
    }

    // İşlem tamamlandı
    if (onProgressUpdate) {
      onProgressUpdate({
        currentItem: 'İşlem tamamlandı!',
        progress: 100,
        success: successCount,
        failed: failedCount,
        total: items.length
      });
    }

    return { success: successCount, failed: failedCount, total: items.length };
  }

  /**
   * QR Menu'de bulunan bir ürünün fiyat/miktar bilgisini senkronize et
   * @param companyId - Şirket ID
   * @param item - Güncellenecek ürün
   * @param field - Güncellenecek alan ('quantity' veya 'salePrice')
   * @param newValue - Yeni değer
   * @param getCategoryName - Kategori adı getiren fonksiyon
   * @param getBrandName - Marka adı getiren fonksiyon
   * @param companyInfo - Şirket bilgileri (opsiyonel - AuthContext'ten sağlanabilir)
   * @returns Promise<{ success: boolean; message?: string; error?: string }>
   */
  static async syncQRMenuProduct(
    companyId: string,
    item: any, // InventoryItem
    field: 'quantity' | 'salePrice',
    newValue: number,
    getCategoryName: (id: string | null) => string,
    getBrandName: (id: string | null) => string,
    companyInfo?: CompanyInfo
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    console.log('🔍 [QR MENU SYNC] QR Menü kontrolü yapılıyor...');
    console.log('🔍 [QR MENU SYNC] selectedItem.isQRMenu değeri:', item.isQRMenu);
    console.log('🔍 [QR MENU SYNC] Kontrol sonucu:', item.isQRMenu === true);
    
    if (item.isQRMenu !== true) {
      console.log('ℹ️ [QR MENU SYNC] Ürün QR Menü\'de değil, senkronizasyon atlandı');
      return { success: true, message: 'Ürün QR Menü\'de değil' };
    }

    console.log('✅ [QR MENU SYNC] Ürün QR Menü\'de mevcut, SQL güncelleniyor...');
    
    try {
      // ✅ companyInfo parametreden alınır (Firebase yerine)
      const info = companyInfo || { name: 'Bilinmeyen Şirket' };
      
      // .NET API'ye güncellenmiş ürün bilgilerini gönder
      const payload = {
        companyInfo: {
          firebaseId: companyId,
          name: info.name || 'Bilinmeyen Şirket',
          email: info.email || '',
          phone: info.phone || '',
          address: info.address || '',
          taxNumber: info.taxNumber || '',
          createdAt: info.createdAt ? new Date(info.createdAt).toISOString() : new Date().toISOString()
        },
        stockInfo: {
          firebaseId: item.id,
          name: item.name,
          stockCode: item.stockCode,
          barcode: item.barcode || null,
          categoryName: getCategoryName(item.categoryId || null),
          brandName: getBrandName(item.brandId || null),
          // ✅ YENİ SİSTEM: WarehouseStocks'tan toplam hesapla
          quantity: field === 'quantity' 
            ? newValue 
            : (item.warehouseStocks?.reduce((sum: number, ws: any) => sum + ws.quantity, 0) || 0),
          unit: item.unit,
          salePrice: field === 'salePrice' ? newValue : item.salePrice,
          saleCurrency: item.saleCurrency || 'TRY',
          description: item.description || '',
          imageUrl: item.imageUrl || null,
          isActive: item.isActive,
          createdAt: item.createdAt ? new Date(item.createdAt).toISOString() : new Date().toISOString()
        }
      };

      console.log('📤 [QR MENU SYNC] Payload:', JSON.stringify(payload, null, 2));

      await mainApi.post('/api/QRMenu/send-stock', payload);

      console.log('✅ [QR MENU SYNC] QR Menü SQL güncellendi');
      
      return { 
        success: true, 
        message: `${field === 'quantity' ? 'Miktar' : 'Satış fiyatı'} güncellendi ve QR Menü senkronize edildi!` 
      };
    } catch (qrMenuError) {
      console.error('❌ [QR MENU SYNC] QR Menü senkronizasyon hatası:', qrMenuError);
      return { 
        success: false, 
        error: qrMenuError instanceof Error ? qrMenuError.message : 'QR Menü senkronizasyonu sırasında bir hata oluştu' 
      };
    }
  }
}
